import React, { useEffect, useState, useCallback } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { useFileUtils } from '../../hooks/useFileUtils';
import './FullscreenImagePreview.scss';

const FullscreenImagePreview = ({
    attachments,
    currentIndex,
    onClose,
    onIndexChange
}) => {
    const { t } = useLanguage();
    const [activeIndex, setActiveIndex] = useState(currentIndex);


    // Update active index when currentIndex prop changes
    useEffect(() => {
        setActiveIndex(currentIndex);
    }, [currentIndex]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event) => {
            switch (event.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    handlePrevious();
                    break;
                case 'ArrowRight':
                    handleNext();
                    break;
                default:
                    break;
            }
        };

        // Prevent body scroll
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = '';
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeIndex, attachments.length]);

    const handlePrevious = useCallback(() => {
        if (attachments.length <= 1) return;

        const newIndex = activeIndex > 0 ? activeIndex - 1 : attachments.length - 1;
        setActiveIndex(newIndex);
        onIndexChange?.(newIndex);
    }, [activeIndex, attachments.length, onIndexChange]);

    const handleNext = useCallback(() => {
        if (attachments.length <= 1) return;

        const newIndex = activeIndex < attachments.length - 1 ? activeIndex + 1 : 0;
        setActiveIndex(newIndex);
        onIndexChange?.(newIndex);
    }, [activeIndex, attachments.length, onIndexChange]);

    const handleBackdropClick = (e) => {
        if (e.target.classList.contains('fullscreen-image-preview')) {
            onClose();
        }
    };

    const handleDownload = () => {
        const attachment = attachments[activeIndex];
        if (!attachment?.file) return;

        const link = document.createElement('a');
        link.href = getFileUrl();
        link.download = attachment.file.filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (!attachments || attachments.length === 0 || activeIndex < 0 || activeIndex >= attachments.length) {
        return null;
    }

    const currentAttachment = attachments[activeIndex];
    const { file } = currentAttachment;

    const fileUtils = useFileUtils(currentAttachment?.file, Date.now()); // eslint-disable-line react-hooks/rules-of-hooks
    const {
        getFileUrl,
    } = fileUtils;

    return (
        <div className="fullscreen-image-preview" onClick={handleBackdropClick}>
            <div className="preview-container">
                {/* Header with title and counter */}
                <div className="preview-header">
                    <div className="preview-title">
                        {attachments.length > 1 && (
                            <span className="image-counter">
                                {activeIndex + 1} / {attachments.length}
                            </span>
                        )}
                    </div>

                    <div className="preview-actions">
                        <button
                            onClick={handleDownload}
                            className="action-btn download-btn"
                            title={t('attachments.download')}
                        >
                            ⬇️
                        </button>

                        <button
                            onClick={onClose}
                            className="action-btn close-btn"
                            title={t('common.close')}
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {/* Main image display */}
                <div className="image-container">
                    <img
                        src={getFileUrl()}
                        alt={file.filename}
                        className="fullscreen-image"
                        draggable={false}
                    />
                </div>

                {/* Navigation arrows (only if multiple images) */}
                {attachments.length > 1 && (
                    <>
                        <button
                            onClick={handlePrevious}
                            className="nav-arrow nav-arrow-left"
                            title={t('common.previous')}
                        >
                            <svg viewBox="0 0 24 24" className="arrow-icon">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        </button>

                        <button
                            onClick={handleNext}
                            className="nav-arrow nav-arrow-right"
                            title={t('common.next')}
                        >
                            <svg viewBox="0 0 24 24" className="arrow-icon">
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </button>
                    </>
                )}

                {/* Bottom thumbnail navigation (only if multiple images) */}
                {attachments.length > 1 && (
                    <div className="thumbnail-navigation">
                        <div className="thumbnail-strip">
                            {attachments.map((attachment, index) => {
                                const fileUtilsThumb = useFileUtils(attachment?.file, Date.now(), 80); // eslint-disable-line react-hooks/rules-of-hooks
                                const {
                                    getFileUrl: getFileUrlThumb,
                                } = fileUtilsThumb;
                                return (
                                <button
                                    key={attachment.id}
                                    onClick={() => {
                                        setActiveIndex(index);
                                        onIndexChange?.(index);
                                    }}
                                    className={`thumbnail-nav ${index === activeIndex ? 'active' : ''}`}
                                >
                                    <img
                                        src={getFileUrlThumb()}
                                        alt={attachment.file.filename}
                                        className="thumbnail-image"
                                    />
                                </button>
                            )})}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FullscreenImagePreview;